/* eslint-disable react/jsx-indent */
// Components==============
import Button from 'components/UI/Button/Button';
import Modal from 'components/UI/Modal/Modal';
import Search from 'components/UI/Search/Search';
import Spinner from 'components/UI/Spinner/Spinner';
import useSearchUsers from 'hooks/useSearchUsers';
import { useTranslate } from 'hooks/useTranslate';
import React, { createContext, useContext, useEffect, useState } from 'react';
import './ReviewerSelectModal.module.scss';
import { useDispatch } from 'react-redux';
import storeAssessment from 'actions/assessment/store';
import Reviewer from './Reviewer/Reviewer';
import { OverviewContext } from '../../Views/Overview';
// =========================

type ReviewerAddModalContextType = {
  selectedFirstReviewer: UserEntity[];
  setSelectedFirstReviewer: React.Dispatch<React.SetStateAction<UserEntity[]>>;
  selectedSecondReviewer: UserEntity[];
  setSelectedSecondReviewer: React.Dispatch<React.SetStateAction<UserEntity[]>>;
  firstReviewerValue: string;
  setFirstReviewerValue: React.Dispatch<React.SetStateAction<string>>;
  secondReviewerValue: string;
  setSecondReviewerValue: React.Dispatch<React.SetStateAction<string>>;
};

export type ShouldDispatchType = 'initial' | 'pending' | 'dispatch';

export const ReviewerAddModalContext = createContext(
  {} as ReviewerAddModalContextType
);

const firstReviewerFilter = (user: UserEntity) => {
  const roles = user.roles.map((r) => r.name);
  return (
    roles.includes('Docent') ||
    roles.includes('Extern') ||
    roles.includes('super-admin')
  );
};

const secondReviewerFilter = (user: UserEntity) => {
  const roles = user.roles.map((r) => r.name);
  return roles.includes('Docent') || roles.includes('super-admin');
};

export default function ReviewerAddModal({
  projectId,
}: {
  projectId?: number;
}) {
  const { setReviewerModalIsOpen, setIsRequested } = useContext(
    OverviewContext
  );
  const translate = useTranslate();
  const dispatch = useDispatch();

  const [firstReviewerValue, setFirstReviewerValue] = useState('');
  const [secondReviewerValue, setSecondReviewerValue] = useState('');
  const [searchResultsFirstReviewer, setSearchResultsFirstReviewer] = useState<
    UserEntity[]
  >([]);
  const [
    searchResultsSecondReviewer,
    setSearchResultsSecondReviewer,
  ] = useState<UserEntity[]>([]);
  const [selectedFirstReviewer, setSelectedFirstReviewer] = useState<
    UserEntity[]
  >([]);
  const [selectedSecondReviewer, setSelectedSecondReviewer] = useState<
    UserEntity[]
  >([]);

  const [
    shouldDispatchFirstReviewer,
    setShouldDispatchFirstReviewer,
  ] = useState<ShouldDispatchType>('initial');
  const [
    shouldDispatchSecondReviewer,
    setShouldDispatchSecondReviewer,
  ] = useState<ShouldDispatchType>('initial');

  useSearchUsers({
    setSearchResults: setSearchResultsFirstReviewer,
    setShouldDispatch: setShouldDispatchFirstReviewer,
    shouldDispatch: shouldDispatchFirstReviewer,
    value: firstReviewerValue,
    includeExtern: true,
    searchFilter: firstReviewerFilter,
    projectId,
  });

  useSearchUsers({
    setSearchResults: setSearchResultsSecondReviewer,
    setShouldDispatch: setShouldDispatchSecondReviewer,
    shouldDispatch: shouldDispatchSecondReviewer,
    value: secondReviewerValue,
    searchFilter: secondReviewerFilter,
    projectId,
  });

  const hasContentFirstReviewer = !!(
    selectedFirstReviewer.length <= 0 && searchResultsFirstReviewer.length
  );
  const hasContentSecondReviewer = !!(
    selectedSecondReviewer.length <= 0 && searchResultsSecondReviewer.length
  );

  const createAssessment = () => {
    dispatch(
      storeAssessment({
        first_assessor: selectedFirstReviewer[0]?.id,
        second_assessor: selectedSecondReviewer[0]?.id,
        project_id: projectId,
      })
    );
    setReviewerModalIsOpen(false);
    setIsRequested(true);
  };

  return (
    <ReviewerAddModalContext.Provider
      value={{
        selectedFirstReviewer,
        setSelectedFirstReviewer,
        selectedSecondReviewer,
        setSelectedSecondReviewer,
        firstReviewerValue,
        setFirstReviewerValue,
        secondReviewerValue,
        setSecondReviewerValue,
      }}
    >
      <Modal setModalIsOpen={setReviewerModalIsOpen} size="medium">
        <h1 styleName="title ">
          Belangrijk, voor je tot beoordelen overgaat!{' '}
          <Spinner
            loading={
              shouldDispatchFirstReviewer === 'pending' ||
              shouldDispatchSecondReviewer === 'pending'
            }
          />
        </h1>
        <div styleName="content">
          <div styleName="top">
            <p>
              Zodra je een beoordeling hebt aangevraagd is het niet meer
              mogelijk om het project van het slot te halen. Ook je reflectie
              kan niet meer worden bijgewerkt. Zorg er dus voor dat alles
              helemaal klopt voor je je beoordeling aanvraagt!
            </p>
          </div>
          <hr styleName="divider" />
          <div styleName="reviewer">
            <div styleName="review-top">
              <h2>Wie is jouw eerste beoordelaar?</h2>
              <p>
                Geef aan welke docent of externe jij het recht geeft je project
                te mogen beoordelen.
              </p>
            </div>
            <div styleName="search">
              <Search
                value={firstReviewerValue}
                setValue={setFirstReviewerValue}
                fluid
              />
            </div>
          </div>
          {hasContentFirstReviewer ? (
            <div styleName="body">
              {searchResultsFirstReviewer.map((reviewer) => (
                <Reviewer type="first" reviewer={reviewer} key={reviewer.id} />
              ))}
            </div>
          ) : firstReviewerValue &&
            shouldDispatchFirstReviewer === 'initial' &&
            selectedFirstReviewer.length <= 0 ? (
            <div styleName="no-results">
              <h2>
                {translate('project.groupswork-popups.no-search-results')}
              </h2>
              <p>
                {translate(
                  'project.groupswork-popups.no-search-results-description'
                )}
              </p>
            </div>
          ) : selectedFirstReviewer.length >= 1 ? (
            <div styleName="body">
              {selectedFirstReviewer.map((reviewer) => (
                <Reviewer type="first" reviewer={reviewer} key={reviewer.id} />
              ))}
              {searchResultsFirstReviewer
                .filter(
                  (reviewer) =>
                    !selectedFirstReviewer.includes(reviewer) &&
                    !selectedSecondReviewer.includes(reviewer)
                )
                .map((reviewer) => (
                  <Reviewer
                    type="first"
                    reviewer={reviewer}
                    key={reviewer.id}
                  />
                ))}
            </div>
          ) : (
            <>
              <Reviewer type="no-select" />
            </>
          )}
          <hr styleName="divider" />
          <div styleName="reviewer">
            <div styleName="review-top">
              <h2>Wie is jouw tweede beoordelaar?</h2>
              <p>
                Geef aan welke docent het oordeel van de eerste beoordelaar
                valideert.
              </p>
            </div>
            <div styleName="search">
              <Search
                value={secondReviewerValue}
                setValue={setSecondReviewerValue}
                fluid
              />
            </div>
          </div>
          {hasContentSecondReviewer ? (
            <div styleName="body">
              {searchResultsSecondReviewer.map((reviewer) => (
                <Reviewer type="second" reviewer={reviewer} key={reviewer.id} />
              ))}
            </div>
          ) : secondReviewerValue &&
            shouldDispatchSecondReviewer === 'initial' &&
            selectedSecondReviewer.length <= 0 ? (
            <div styleName="no-results">
              <h2>
                {translate('project.groupswork-popups.no-search-results')}
              </h2>
              <p>
                {translate(
                  'project.groupswork-popups.no-search-results-description'
                )}
              </p>
            </div>
          ) : selectedSecondReviewer.length >= 1 ? (
            <div styleName="body">
              {selectedSecondReviewer.map((reviewer) => (
                <Reviewer type="second" reviewer={reviewer} key={reviewer.id} />
              ))}
              {searchResultsSecondReviewer
                .filter(
                  (reviewer) =>
                    !selectedSecondReviewer.includes(reviewer) &&
                    !selectedFirstReviewer.includes(reviewer)
                )
                .map((reviewer) => (
                  <Reviewer
                    type="second"
                    reviewer={reviewer}
                    key={reviewer.id}
                  />
                ))}
            </div>
          ) : (
            <>
              <Reviewer type="no-select" />
            </>
          )}
          <div styleName="button-wrapper">
            <Button color="grey" onClick={() => setReviewerModalIsOpen(false)}>
              {translate('general.terms.cancel')}
            </Button>
            <Button
              onClick={createAssessment}
              inactive={
                !selectedFirstReviewer.length || !selectedSecondReviewer.length
              }
            >
              Beoordeling aanvragen
            </Button>
          </div>
        </div>
      </Modal>
    </ReviewerAddModalContext.Provider>
  );
}
